import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    DatePicker,
    Button,
    Card,
    Upload,
    message,
    Skeleton,
    Space,
    Table,
    Empty,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InboxOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useAxios } from "../../hooks/hooks";
import config from "../../business/config.json";

const { TextArea } = Input;
const { Dragger } = Upload;

function ClearanceReportPage() {
    const { id } = useParams();
    const axios = useAxios();
    const [form] = Form.useForm();

    const [loaded, setLoaded] = useState(false);
    const [project, setProject] = useState(null);
    const [reports, setReports] = useState([]);
    const [pendingFile, setPendingFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchProject = async () => {
        // Staff have project-view; customers only have customer-projects.
        for (const namespace of ["project-view", "customer-projects"]) {
            try {
                const response = await axios.current.get(`/${namespace}/` + id);
                const row = Array.isArray(response.data) ? response.data[0] : null;
                if (row) return row;
            } catch (e) {
                console.error(e);
            }
        }
        return null;
    };

    const fetchReports = async () => {
        try {
            const response = await axios.current.get(`/project/${id}/clearance-report`);
            return (response.data && response.data.reports) || [];
        } catch (e) {
            console.error(e);
            return [];
        }
    };

    useEffect(() => {
        if (loaded || !axios.current) return;
        Promise.all([fetchProject(), fetchReports()]).then(([p, r]) => {
            setProject(p);
            setReports(r);
            setLoaded(true);
        });
    }, [loaded, axios]);

    const onSubmit = async (values) => {
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append(
                "clearance_date",
                values.clearance_date ? values.clearance_date.format("YYYY-MM-DD") : ""
            );
            formData.append("contractor_company", values.contractor_company || "");
            formData.append("inspector_name", values.inspector_name || "");
            formData.append("notes", values.notes || "");
            if (pendingFile) {
                formData.append("file", pendingFile);
            }

            const response = await axios.current.post(
                `/project/${id}/clearance-report`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (response.data && response.data.error) {
                message.error("Failed to save clearance report");
                console.error(response.data.error);
            } else {
                message.success("Clearance report saved");
                form.resetFields();
                setPendingFile(null);
                const refreshed = await fetchReports();
                setReports(refreshed);
            }
        } catch (e) {
            console.error(e);
            message.error("Failed to save clearance report");
        } finally {
            setSubmitting(false);
        }
    };

    const draggerProps = {
        name: "file",
        multiple: false,
        accept: ".pdf",
        beforeUpload: (file) => {
            setPendingFile(file);
            return false;
        },
        onRemove: () => setPendingFile(null),
        fileList: pendingFile ? [pendingFile] : [],
    };

    if (!loaded) {
        return (
            <>
                <PageHeader title="Clearance Report" style={{ padding: 0, paddingBottom: 16 }} />
                <Skeleton active />
            </>
        );
    }

    const columns = [
        {
            title: "Clearance Date",
            dataIndex: "clearance_date",
            key: "clearance_date",
            render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
        },
        {
            title: "Contractor / Company",
            dataIndex: "contractor_company",
            key: "contractor_company",
        },
        {
            title: "Inspector",
            dataIndex: "inspector_name",
            key: "inspector_name",
        },
        {
            title: "Notes",
            dataIndex: "notes",
            key: "notes",
            ellipsis: true,
        },
        {
            title: "File",
            key: "file",
            render: (_, record) =>
                record.file_exists ? (
                    <Button
                        type="link"
                        href={`${config.api_domain}${record.url}`}
                        target="_blank"
                        size="small"
                    >
                        <FontAwesomeIcon icon="download" className="fa-fw" />&nbsp; Download
                    </Button>
                ) : (
                    <span style={{ color: "#999" }}>No file</span>
                ),
        },
    ];

    return (
        <>
            <PageHeader
                title="Clearance Report"
                subTitle={project ? `${project.place_project || ""} (${id})` : `Project ${id}`}
                style={{ padding: 0, paddingBottom: 16 }}
            />
            <Card title="Existing Clearance Reports" style={{ marginBottom: 16 }}>
                {reports.length === 0 ? (
                    <Empty description="No clearance reports yet" />
                ) : (
                    <Table
                        rowKey="id_clearance_report"
                        dataSource={reports}
                        columns={columns}
                        pagination={false}
                        size="small"
                    />
                )}
            </Card>
            <Card title="Add Clearance Report">
                <Form form={form} layout="vertical" onFinish={onSubmit}>
                    <Form.Item
                        label="Clearance Date"
                        name="clearance_date"
                        rules={[{ required: true, message: "Please select a clearance date" }]}
                    >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                        label="Contractor / Company"
                        name="contractor_company"
                        rules={[{ required: true, message: "Please enter the contractor or company" }]}
                    >
                        <Input
                            prefix={<FontAwesomeIcon icon="building" />}
                            placeholder="Company that performed the clearance"
                        />
                    </Form.Item>
                    <Form.Item label="Inspector Name" name="inspector_name">
                        <Input
                            prefix={<FontAwesomeIcon icon="user" />}
                            placeholder="Person who signed off"
                        />
                    </Form.Item>
                    <Form.Item label="Notes" name="notes">
                        <TextArea rows={4} placeholder="Anything worth recording about this clearance" />
                    </Form.Item>
                    <Form.Item label="Clearance Report PDF">
                        <Dragger {...draggerProps}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag a PDF here to upload
                            </p>
                        </Dragger>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>
                                <FontAwesomeIcon icon="save" className="fa-fw" />&nbsp; Save
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </>
    );
}

export default ClearanceReportPage;